import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { LanguageContext } from '../../../context/LanguageContext';
import Layout from '../../../components/admin/Layout';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useContext(LanguageContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'cashier',
    is_active: true,
    is_one_checked: false,
    user_number: '',
  });

  const isRTL = language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await axios.get(`/users/${id}`);
        const user = response.data.user;
        setFormData({
          name: user.name,
          email: user.email,
          password: '',
          role: user.role,
          is_active: user.is_active,
          is_one_checked: user.is_one_checked || false,
          user_number: user.user_number || '',
        });
      } catch (error) {
        toast.error(isRTL ? 'فشل تحميل بيانات المستخدم' : 'Failed to load user data');
        console.error('Error loading user:', error);
        navigate('/admin/users');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id, isRTL, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const updateData = { ...formData };
    if (!updateData.password) {
      delete updateData.password;
    }

    try {
      await axios.put(`/users/${id}`, updateData);
      toast.success(isRTL ? 'تم تحديث المستخدم بنجاح' : 'User updated successfully');
      navigate('/admin/users');
    } catch (error) {
      const message = error.response?.data?.message || (isRTL ? 'فشل تحديث المستخدم' : 'Failed to update user');
      toast.error(message);
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-lg text-gray-600">
            {isRTL ? 'جاري التحميل...' : 'Loading...'}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6" dir={dir}>
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 p-6">
            <h1 className="text-2xl font-bold text-white">
              {isRTL ? 'تعديل المستخدم' : 'Edit User'}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Number Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {isRTL ? 'رقم المستخدم' : 'User Number'}
                </label>
                <input
                  type="text"
                  name="user_number"
                  value={formData.user_number}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-150 ease-in-out"
                  dir="ltr"
                />
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {isRTL ? 'الاسم' : 'Name'}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-150 ease-in-out"
                  dir={dir}
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {isRTL ? 'البريد الإلكتروني' : 'Email'}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-150 ease-in-out"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {isRTL ? 'كلمة المرور (اختياري)' : 'Password (Optional)'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isRTL ? 'اتركه فارغاً لعدم التغيير' : 'Leave empty to keep unchanged'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-150 ease-in-out"
                dir="ltr"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Role Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {isRTL ? 'الدور' : 'Role'}
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition duration-150 ease-in-out appearance-none bg-white
                    ${isRTL 
                      ? 'pl-10 bg-[length:1.5em_1.5em] bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")] bg-[position:left_0.5rem_center] bg-no-repeat' 
                      : 'pr-10 bg-[length:1.5em_1.5em] bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")] bg-[position:right_0.5rem_center] bg-no-repeat'
                    }`}
                  dir={dir}
                >
                  <option value="cashier">{isRTL ? 'كاشير' : 'Cashier'}</option>
                  <option value="admin">{isRTL ? 'مدير' : 'Admin'}</option>
                </select>
              </div>

              {/* Active Status */}
              <div className="flex items-center h-full">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                  <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-sm font-medium text-gray-700`}>
                    {isRTL ? 'نشط' : 'Active'}
                  </span>
                </label>
              </div>

              {/* 1 Checked Status */}
              <div className="flex items-center h-full">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_one_checked"
                    checked={formData.is_one_checked}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                  <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-sm font-medium text-gray-700`}>
                    {isRTL ? '1 مفعل' : '1 checked'}
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-end gap-4 pt-6 border-t`}>
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-150 ease-in-out"
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-yellow-300 disabled:cursor-not-allowed transition duration-150 ease-in-out"
              >
                {saving 
                  ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
                  : (isRTL ? 'حفظ التغييرات' : 'Save Changes')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditUser;
